import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/lib/supabase';
import { Progress } from '@/components/ui/progress';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { TileEmpty } from '../TileWrapper';
import { Folder, Users, CheckSquare } from 'lucide-react';
import { cn } from '@/lib/utils';

// Mock data for simulation
const MOCK_SPACES = [
  { id: '1', name: 'Site Alpha', color: '#6366f1', compliance: 92, teamCount: 12, taskCount: 5 },
  { id: '2', name: 'Main Office', color: '#22c55e', compliance: 85, teamCount: 24, taskCount: 8 },
  { id: '3', name: 'Warehouse B', color: '#f59e0b', compliance: 68, teamCount: 8, taskCount: 12 },
  { id: '4', name: 'Distribution Center', color: '#ef4444', compliance: 54, teamCount: 15, taskCount: 3 },
  { id: '5', name: 'North Site', color: '#8b5cf6', compliance: 78, teamCount: 6, taskCount: 0 },
  { id: '6', name: 'South Site', color: '#ec4899', compliance: 95, teamCount: 10, taskCount: 2 },
];

export function MySpacesTile({ filter, size, useMockData }) {
  const navigate = useNavigate();
  const { company, userProfile } = useAuth();
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (useMockData) {
      const limit = size === 'xl' ? 5 : size === 'large' ? 5 : 3;
      setData(MOCK_SPACES.slice(0, limit));
      setLoading(false);
      return;
    }

    if (!company?.id || !userProfile?.id) return;

    const loadData = async () => {
      setLoading(true);
      try {
        const { data: dashData, error } = await supabase.rpc('get_dashboard_data_v2', {
          p_company_id: company.id,
          p_user_id: userProfile.id,
          p_include_archived: false,
          p_limit: size === 'xl' ? 9 : size === 'large' ? 6 : 4,
          p_offset: 0,
          p_search: null,
        });

        if (error) throw error;

        const projects = (dashData?.projects || []).map(p => ({
          id: p.id,
          name: p.name,
          color: p.color || '#6366f1',
          compliance: p.compliance_summary?.percentage || 0,
          teamCount: p.team_count || 0,
          taskCount: p.open_tasks || 0,
        }));

        setData(projects);
      } catch (err) {
        console.error('Failed to load spaces:', err);
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [company?.id, userProfile?.id, size, useMockData]);

  const getComplianceColor = (pct) => {
    if (pct >= 90) return 'bg-green-500';
    if (pct >= 70) return 'bg-amber-500';
    return 'bg-red-500';
  };

  if (loading) {
    return (
      <div className={cn(
        "grid gap-3",
        size === 'xl' ? 'grid-cols-3' : size === 'large' ? 'grid-cols-2' : 'grid-cols-2'
      )}>
        {[...Array(size === 'xl' ? 6 : 4)].map((_, i) => (
          <div key={i} className="h-24 bg-gray-100 rounded-lg animate-pulse" />
        ))}
      </div>
    );
  }

  if (data.length === 0) {
    return <TileEmpty message="No spaces found" icon={Folder} />;
  }

  const isLarge = size === 'large' || size === 'xl';

  return (
    <ScrollArea className="h-full">
      <div className={cn(
        "grid gap-3 content-start pr-2",
        size === 'xl' ? 'grid-cols-3' : 'grid-cols-2'
      )}>
        {data.map((space) => (
          <div
            key={space.id}
            onClick={() => navigate(`/space/${space.id}`)}
            className="bg-white border rounded-lg p-3 cursor-pointer hover:shadow-md transition-shadow"
          >
            {/* Header */}
            <div className="flex items-start gap-2 mb-2">
              <div
                className="w-8 h-8 rounded-lg flex items-center justify-center shrink-0"
                style={{ backgroundColor: space.color + '20' }}
              >
                <Folder className="h-4 w-4" style={{ color: space.color }} />
              </div>
              <div className="min-w-0 flex-1">
                <p className="text-sm font-medium truncate">{space.name}</p>
                <p className={cn(
                  "text-lg font-bold",
                  space.compliance >= 90 ? 'text-green-600' :
                  space.compliance >= 70 ? 'text-amber-600' : 'text-red-600'
                )}>
                  {space.compliance}%
                </p>
              </div>
            </div>

            {/* Progress bar */}
            <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden mb-2">
              <div
                className={cn('h-full transition-all', getComplianceColor(space.compliance))}
                style={{ width: `${space.compliance}%` }}
              />
            </div>

            {/* Stats */}
            {isLarge && (
              <div className="flex items-center gap-3 text-xs text-gray-500">
                <span className="flex items-center gap-1">
                  <Users className="h-3 w-3" />
                  {space.teamCount}
                </span>
                {space.taskCount > 0 && (
                  <span className="flex items-center gap-1">
                    <CheckSquare className="h-3 w-3" />
                    {space.taskCount}
                  </span>
                )}
              </div>
            )}
          </div>
        ))}

        {/* View all button */}
        <div
          onClick={() => navigate('/spaces')}
          className="border border-dashed rounded-lg p-3 cursor-pointer hover:bg-gray-50 transition-colors flex items-center justify-center text-gray-500 text-sm"
        >
          View All
        </div>
      </div>
    </ScrollArea>
  );
}
